import {cloneDeep} from 'lodash';
import Env from '../util/env';
import {clearErrors} from '../actions/erroractions';
import {UpdateInstructionAsync} from '../actions/GroupAudit/GroupInstructions';
import {labels} from '../util/utils';
import {clientSideEventNames, documentTypes, formBodyType, serviceURL} from '../util/uiconstants';
import {updateDocument} from '../actions/Document/documentactions';
import {saveUploadDocumentId} from '../actions/Document/bulkRelateActions';
import {getRelatedDocument} from '../actions/FormBodyRelatedDocuments/formbodyrelateddocumentsactions';
import {dispatchUploadInfoEvent, dispatchUploadErrorEvent} from '../util/uploadEventUtils';
import {checkIsMFE} from '../util/customHooks';

const checkListBodyTypes = [
	formBodyType.DocumentUploadPICApprovalRequired,
	formBodyType.DocumentUploadEQRApprovalRequired,
	formBodyType.DocumentUploadPICEQRApprovalRequired,
	formBodyType.DocumentUploadAnyApprovalRequired
];

export const hooks = {
	ares: {
		onCreatingDocumentMetadata: ({engagementId, userId, parentName, fileId, file, documentTypeId}) => {
			return {
				type: 'KRONOS_UPCHUNK_CREATING_DOCUMENT_METADATA',
				engagementId,
				userId,
				parentName,
				fileId,
				fileName: file.name,
				documentTypeId
			};
		},
		onCreateDocumentMetadata: ({
			documentTypeId,
			documentSourceId,
			engagementId,
			userId,
			parentName,
			file,
			hash,
			fileId,
			overwrite = false,
			entityUid
		}) => {
			return {
				type: 'KRONOS_UPCHUNK_CREATE_DOCUMENT_METADATA',
				parentName,
				meta: {
					api: {
						method: 'POST',
						url: Env.getDocServiceUrl('documents.json'),
						data: null,
						headers: {
							EngagementId: engagementId,
							DocumentTypeId: documentTypeId,
							documentSourceid: documentSourceId,
							isOverwrite: `${overwrite}`,
							TransactionID: userId,
							'X-MD5-HASH': hash,
							'Content-Disposition': `attachment; filename=${encodeURIComponent(file.name)}; size=${file.size}`,
							'Content-Length': 0
						}
					}
				},
				data: {
					entityUid: entityUid,
					file: file,
					fileId: fileId
				},
				payload: {
					supressErrorOnUi: true // to suppress error in error summary
				}
			};
		},
		onCreateError: (action, callbacks, meta, next) => {
			if (callbacks && typeof callbacks.onDuplicate === 'function') {
				if (action && action?.error?.response?.status === 412) {
					next(
						callbacks.onDuplicate(
							action.data.fileId,
							action.data.file || meta.upload.file,
							action.error.response.data,
							callbacks.onOverwrite
						)
					);
				}
			}
			next(action);
		},
		onDocumentUploadCancelled: () => {
			return {
				type: 'KRONOS_UPCHUNK_DOCUMENT_UPLOAD_CANCELLED',
				meta: {}
			};
		},
		onDocumentUploadProgress: ({progress, parentName, documentId, file}) => {
			const normalizedProgress = Math.min(Math.round(progress), 99);
			return {
				type: 'KRONOS_UPCHUNK_DOCUMENT_UPLOAD_PROGRESS',
				parentName,
				meta: {
					upchunk: {
						payload: {
							documentId,
							fileName: file.name,
							inProgress: normalizedProgress < 100,
							progressPercent: normalizedProgress,
							totalPercentage: 100,
							relatedEntityId: file._uploadTag.task
						},
						message: {
							FileId: documentId,
							FileName: file.name
						}
					}
				}
			};
		},
		onDocumentUploadComplete: ({
			engagementId,
			userId,
			parentName,
			documentId,
			documentSourceId,
			documentVersionCount,
			file,
			hash
		}) => {
			return {
				type: 'KRONOS_UPCHUNK_COMPLETE_DOCUMENT_UPLOAD',
				parentName,
				meta: {
					api: {
						method: 'PUT',
						url: Env.getDocServiceUrl(`documents.json/${documentId}`),
						headers: {
							'Content-Type': 'application/octet-stream',
							EngagementId: engagementId,
							TransactionID: userId,
							Range: `bytes=0-${file.size}`,
							'X-MD5-HASH': hash,
							documentSourceid: documentSourceId,
							version_number: documentVersionCount,
							totalfilesize: file.size,
							uploadcomplete: 'true'
						}
					}
				}
			};
		},
		afterDocumentUploadComplete: async (store, action, config, extraProps) => {
			const {file, document} = extraProps;
			const state = store.getState();
			//add here new call needed and getdocumentcall too
			const docData = state.uploadDocuments[`${file.name}_${document.id}`];

			store.dispatch(saveUploadDocumentId(document.id));

			const {
				relatedTasks,
				showCrashDocumentWarningMessage,
				requireValidations,
				signoffRequirements,
				bodyTypeId,
				parentName
			} = action.meta.upload;

			const isCheckListBody = checkListBodyTypes.includes(bodyTypeId) && parentName === 'evidences';

			if (docData) {
				const docDataCopy = cloneDeep(docData);
				var relatedTask = relatedTasks;
				if (!Array.isArray(relatedTasks)) {
					relatedTask = [{taskId: relatedTasks}];
				}
				if (Array.isArray(relatedTask) && relatedTask.length > 0 && !isCheckListBody) {
					const taskIds = relatedTask.map((task) => task.taskId);
					const docType = parentName === 'evidences' ? documentTypes.EVIDENCE : documentTypes.CRASH;
					await taskDocumentAssociation(taskIds, docDataCopy, store, docType);

					if (docData.entityUid) {
						await saveDocumentRelatedObject(docDataCopy, docData.entityUid, store, showCrashDocumentWarningMessage);
					}
				}

				if (signoffRequirements) {
					let payload = signoffRequirements;
					await store.dispatch(
						updateDocument(
							document.engagementId,
							docDataCopy.documentId,
							payload,
							undefined,
							undefined,
							undefined,
							docData.entityUid,
							'FORM_BODY_QUICK_REFERENCE'
						)
					);
				}

				//if the document is uploaded in flowChart section, it will create the relation flowchart step document
				if (
                    state.flowChart &&
                    Object.keys(state.flowChart).length > 0 &&
                    state.flowChart.flowChartDocumentUploadOn &&
                    Object.keys(state.flowChart.flowChartDocumentUploadOn).length !== 0
                ) {
                    window.dispatchEvent(
                        new CustomEvent(clientSideEventNames.documentUploadComplete, {
                            detail: {
                                documentId: docDataCopy.documentId,
                                flowChartStepId: state.flowChart?.flowChartDocumentUploadOn.flowChartStepId,
                                parentName: docDataCopy.parentName
                            }
                        })
                    );
                }

				// if the document is uploaded in group audit section, it will create the relation instruction document
				if (Object.keys(state.groupAudit?.groupAuditDocumentUploadOn).length !== 0) {
					await UpdateInstructionAsync(store, docDataCopy.documentId);
				}
			}

			clearUploadDoc(document.id, file, store);

			if (showCrashDocumentWarningMessage) {
				const isMFE = checkIsMFE();
				store.dispatch(clearErrors());
				
				// Dispatch custom event to be handled by FileUpload component
				// This will emit the message to the parent app via message hub when in MFE mode
				dispatchUploadInfoEvent(labels.noTaskAssociatedToastMessage, 0, isMFE, store.dispatch);
				
				store.dispatch({
					type: 'REMOVE_TASK_DOCUMENT',
					documentName: `${docData.documentName}.${docData.fileExt}`,
					entityUid: docData.entityUid,
					isOnline: true,
					isDragAndDrop: true
				});
			}

			if (requireValidations) {
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			}
		},
		onAPIError: (error, extraProps) => {
			const {fileId, file} = extraProps;
			const errorMessage = error.response?.data?.ErrorMessage || error.response?.data?.errorMessage || '';
			const args = [error.response.status, file.name, errorMessage];

			// Dispatch a custom event to notify listeners about the upload error
			// This can be caught by React components that can properly use the useMessageHubEmit hook
			dispatchUploadErrorEvent(error, fileId, file.name);

			return {
				type: 'KRONOS_UPCHUNK_UPLOAD_ERROR',
				parentName: error.parentName,
				error: args,
				meta: {
					fileId,
					documentId: error.response.documentId,
					errorDetails: error
				}
			};
		}
	}
};

const taskDocumentAssociation = async function(taskIds, docData, store, docType) {
	const state = store.getState();

	// Few of the required properties are picked up from Dochelper message and other from docHelperDocuments store prop
	const currentEngagement = state.engagement;

	const url = `${Env.getBaseURL(serviceURL, currentEngagement.engagementId)}TaskDocuments`;
	const data = {
		documentId: docData.documentId,
		taskIds,
		targetDocumentTypeId: docType
	};

	// This will always be a task document association (POST) as this is on upload Complete
	await store.dispatch({
		type: 'ASSOCIATE_TASK_DOCUMENTS',
		meta: {
			api: {
				url,
				method: 'POST',
				data
			}
		},
		documentName: docData.documentName,
		entityUId: docData.entityUid
	});
};

const clearUploadDoc = (documentId, file, store) => {
	store.dispatch({
		type: 'KRONOS_UPCHUNK_UPLOAD_COMPLETE',
		meta: {
			documentId: documentId,
			file: file
		}
	});
};

const saveDocumentRelatedObject = async function(docData, entityUid, store, skipDocumentCountUpdate = false) {
	const state = store.getState();

	const baseUrl = Env.getBaseURL(serviceURL, state.engagement.engagementId);

	const url = `${baseUrl}Forms/${state.documentId}/Bodies/${entityUid}/References`;

	await store.dispatch({
		type: 'CREATE_RELATED_DOCUMENT',
		meta: {
			api: {
				url: url,
				data: {
					RelatedDocumentId: docData.documentId
				},
				method: 'POST'
			},
			tabname: 'evidences',
			entityUId: entityUid,
			sectionId: docData.sectionId
		},
		skipDocumentCountUpdate
	});
};
